import { useEffect } from "react";
import { getRouteMetadata } from "@/seo/getRouteMetadata";

export const SEO = ({ path, title, description, canonical, type = "website", noindex = false, image, breadcrumbs, publishedTime, modifiedTime }) => {
  const siteName = "FuturesFury";
  const baseUrl = "https://futuresfury.com";
  const defaultTitle = "FuturesFury - Compare Prop Trading Firms | Futures Funded Accounts";
  const defaultDescription = "Compare futures prop trading firms side-by-side. Real pricing, verified trader ratings, detailed rules. Find the best funded futures account for your trading style.";
  const defaultImage = `${baseUrl}/og-default.png`;

  // SSOT: when `path` is provided, route metadata comes from route-metadata.json.
  // SSOT title/description/image take precedence; props remain as fallbacks.
  const ssot = path ? getRouteMetadata(path) : null;
  const resolvedTitle = (ssot && ssot.title) || title;
  const resolvedDescription = (ssot && ssot.description) || description;
  const resolvedCanonical = canonical || (ssot && ssot.canonical) || null;
  const resolvedImage = (ssot && ssot.image) || image;
  const resolvedNoindex = noindex || (ssot ? ssot.noindex : false);

  const pageTitle = resolvedTitle
    ? (resolvedTitle.includes(siteName) ? resolvedTitle : resolvedTitle + " | " + siteName)
    : defaultTitle;
  const cleanCanonical = resolvedCanonical ? resolvedCanonical.replace(/(.+)\/+$/, "$1") : null;
  const fullCanonical = cleanCanonical
    ? (/^https?:\/\//.test(cleanCanonical) ? cleanCanonical : baseUrl + cleanCanonical)
    : null;
  const descriptionText = resolvedDescription || defaultDescription;
  const imageUrl = resolvedImage
    ? (resolvedImage.startsWith("http") ? resolvedImage : baseUrl + resolvedImage)
    : defaultImage;

  useEffect(() => {
    document.title = pageTitle;

    const setOrCreateMeta = (attr, key, val) => {
      if (!val) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };

    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        document.head.appendChild(el);
      }
      el.href = href;
    };

    setOrCreateMeta("name", "description", descriptionText);
    setOrCreateMeta("name", "robots", resolvedNoindex ? "noindex, nofollow" : "index, follow");
    if (fullCanonical) setLink("canonical", fullCanonical);
    setOrCreateMeta("property", "og:type", type);
    setOrCreateMeta("property", "og:title", pageTitle);
    setOrCreateMeta("property", "og:description", descriptionText);
    setOrCreateMeta("property", "og:image", imageUrl);
    setOrCreateMeta("property", "og:url", fullCanonical || (baseUrl + window.location.pathname.replace(/\/+$/, "")));
    setOrCreateMeta("name", "twitter:card", "summary_large_image");
    setOrCreateMeta("name", "twitter:title", pageTitle);
    setOrCreateMeta("name", "twitter:description", descriptionText);
    setOrCreateMeta("name", "twitter:image", imageUrl);

    // Article-specific OG meta
    if (publishedTime) setOrCreateMeta("property", "article:published_time", publishedTime);
    if (modifiedTime) setOrCreateMeta("property", "article:modified_time", modifiedTime);

    // Cleanup article-specific meta on unmount (so non-article pages don't inherit them)
    return () => {
      const pubEl = document.querySelector('meta[property="article:published_time"]');
      if (pubEl) pubEl.remove();
      const modEl = document.querySelector('meta[property="article:modified_time"]');
      if (modEl) modEl.remove();
    };
  }, [pageTitle, descriptionText, resolvedNoindex, imageUrl, fullCanonical, type, publishedTime, modifiedTime]);

  // Dedupe SSR-injected JSON-LD. The seo-middleware (route-meta SSR-light)
  // injects fallback JSON-LD marked with data-ssr-seo for non-JS crawlers.
  // Once React mounts it emits its own (equal or richer) schema, so we remove
  // any SSR block whose @type React also emits — eliminating duplicate
  // BreadcrumbList/etc for JS-rendering crawlers (Google/Bing) with zero loss:
  // SSR-only @types (e.g. firm Product) are kept, and non-JS crawlers — which
  // never run this effect — retain the full SSR fallback.
  useEffect(() => {
    const scripts = Array.from(
      document.querySelectorAll('script[type="application/ld+json"]')
    );
    const reactTypes = new Set();
    scripts.forEach((s) => {
      if (s.hasAttribute("data-ssr-seo")) return;
      try {
        const ty = JSON.parse(s.textContent || "{}")["@type"];
        if (ty) reactTypes.add(ty);
      } catch (e) {
        /* ignore unparsable */
      }
    });
    scripts.forEach((s) => {
      if (!s.hasAttribute("data-ssr-seo")) return;
      try {
        const ty = JSON.parse(s.textContent || "{}")["@type"];
        if (ty && reactTypes.has(ty)) s.remove();
      } catch (e) {
        /* ignore unparsable */
      }
    });
  }, []);

  // Only render JSON-LD breadcrumbs (Organization schema is already in index.html)
  const breadcrumbSchema = breadcrumbs ? JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": b.name,
      "item": baseUrl + b.path,
    })),
  }).replace(/</g, '\\u003c') : null;

  if (!breadcrumbSchema) return null;

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
  );
};

// Separate component for JSON-LD structured data
export const JsonLd = ({ data }) => {
  if (!data) return null;
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(data).replace(/</g, '\\u003c'),
      }}
    />
  );
};
